import { IStudiosAdministrationStudioModel } from "../../../components/administration/studiosAdministration/studio/models/studiosAdministrationStudioModel";
import { UserRole } from "../../enums";
import { getCurrentUserRole } from "../helpers/userHelper";
import { IUserModel } from "../hooks/useAuthentication";
import { dateUtility } from "../utilities/dateUtility";
import { NavigationItems } from "./navigationItems";

export interface IRoute {
  name: string;
  path: string;
  element: JSX.Element;
  isPublic?: boolean;
  // Renders on its own, outside the Layout: no header, no sidebar and none of the
  // studio-picker / phone-number dialogs. For public pages meant to be opened by people
  // who are not logged-in users of the studio (e.g. a shared link page).
  isStandalone?: boolean;
  authenticationEndpoint?: string;
  RWAuthenticationEndpoint?: string;
  navigationItem?: NavigationItems;
  // When true, a floating back button is shown on this route (mobile/tablet).
  // See BackButton in the Layout.
  showBackButton?: boolean;
  // Where to navigate when there is no in-app history to go back to
  // (e.g. the page was opened via a deep link or a cold PWA start).
  backFallbackPath?: string;
}

export const routePaths = {
  home: "/",
  statistics: "/statistics",
  viewStatistics: "/statistics/:type/:dateFrom/:dateTo/:userIds/:hallIds?",
  login: "/login",
  admin: "/admin",
  googleOAuth: "oauth/google",
  googleOAuthSuccess: "oauth/google/success",
  logout: "/logout",
  resetPassword: "/reset-password",
  setPassword: "/set-password/:token/:email/:studioId",
  impersonate: "/impersonate",
  settings: "/settings",

  dashboard: "/dashboard",

  plannerEvent: "/planner/:id",
  plannerEventDuplication: "/planner/duplicate/:id",
  plannerEventSignUp: "/planner/event/sign-up/:eventId",
  plannerEventEditSignUp: "/planner/event/sign-up/edit/:eventId",
  plannerEventArrivalConfirmation: "/planner/event/confirm/:userEventId",
  plannerAdd: "/planner/add/:day/:month/:year",
  plannerEdit: "/planner/edit/:id",

  studios: "/studios",
  studioSingle: "/studio/:id",
  studioAdd: "/studios/add",
  studioEdit: "/studios/edit/:id",

  tenants: "/tenants",
  tenantSingle: "/tenant/:id",
  tenantAdd: "/tenants/add",
  tenantEdit: "/tenants/edit/:id",

  employees: "/employees",
  employeeSingle: "/employee/:id",
  employeeAdd: "/employee/add",
  employeeEdit: "/employee/edit/:id",

  plans: "/plans",
  planSingle: "/plan/:id",
  planAdd: "/plan/add",
  planEdit: "/plan/edit/:id",

  forms: "/forms",

  families: "/families",
  familySingle: "/family/:id",
  familyAdd: "/family/add",
  familyEdit: "/family/edit/:id",

  users: "/users",
  userSingle: "/user/:id",
  userWorkouts: "/user/:id/workouts",
  userBodyMetrics: "/user/:id/metrics",
  userGoals: "/user/:id/goals",
  userAdd: "/user/add",
  userEdit: "/users/edit/:id",
  userRefillCard: "/users/refillCard",
  userPlan: "/user-plan/:userPlanId",
  userPlanEdit: "/users/refillCard/:userPlanId",

  studioRelations: "/studio-relations",
  studioRelationsAdd: "/studio-relations/add",
  studioRelationsEdit: "/studio-relations/edit",

  giftVouchers: "/administration/gift-vouchers",
  studioReviews: "/administration/reviews",

  communicationLogs: "/administration/communication-logs",
  invoices: "/invoices",

  portalMyProfile: "/my-profile",
  portalMyReservations: "/my-reservations",
  portalMyFamily: "/my-family",
  portalMyFamilyAdd: "/my-family/add/:familyId",
  portalMyFamilyEdit: "/my-family/edit/:familyId",
  portalMyPlans: "/my-plans",
  portalMyWorkouts: "/my-workouts",
  portalMyBodyMetrics: "/my-metrics",
  portalMyGoals: "/my-goals",
  portalMyConsents: "/my-consents",
  portalAppointment: "/appointment",
  review: "/review",
  portalGroupClassWizard: "/group-class-wizard",
  confirmWaitingList: "/confirm-waitinglist",
  portalReservationSuccess: "/reservation-success",

  // Nomenclatures
  nomenclaturePlanTypes: "/plan-types",
  nomenclatureHalls: "/halls",
  nomenclatureBreakType: "/break-type",
  nomenclatureServiceType: "/service-type",
  nomenclatureEventTypeCategory: "/event-type-categories",

  // Cashdesk
  cashdesk: "/cashdesk",

  // Gym
  gym: "/gym",

  // Inventory
  inventoryOverview: "/inventory/overview",
  inventoryProducts: "/inventory/products",
  inventoryTransfers: "/inventory/transfers",

  calendar: "/calendar",
  paymentResult: "/payment-result",

  // "/support" opens the AI assistant for staff when it is enabled (SupportHome), otherwise
  // the tickets list; the tickets list always lives at "/support/tickets" too.
  support: "/support",
  supportTickets: "/support/tickets",
  supportNew: "/support/new",
  supportChat: "/support/:id",
  // Public, shareable link page of a studio (multy.me / Linktree-style). Lives on the
  // studio's own domain and is opened by people who are not logged in, so it is flagged
  // isStandalone and rendered outside the Layout — see App.tsx.
  studioLinkPage: "/l/:slug",
  // Public page a client opens from a form staff emailed them (ClientFormRequest). The token
  // alone identifies the form, the studio and the client, so no login; standalone like the
  // link page. The backend builds the same path (ClientFormService.FillFormPath).
  fillClientForm: "/fill-form/:token",

  errorPage: "/error",
  testPage: "/testPage",
  serverErrorPage: "/serverError",
  noStudiosFoundPage: "/no-studios-found",
  invalidDomain: "/invalid-domain",
};

// Statistics

// URL token used in :hallIds when no hall filter is applied (i.e. "All halls").
export const ALL_HALLS_URL_TOKEN = "all";

// URL token used in :userIds when no user filter is applied (i.e. "All employees").
// Backend ParseUserIds maps this to a null int[], skipping the user filter entirely.
export const ALL_USERS_URL_TOKEN = "all";

export const constructViewStatisticsHref = (
  type: number,
  dateFrom: string,
  dateTo: string,
  userIds: number[],
  hallIds?: number[]
) => {
  const hasSpecificHallSelection =
    !!hallIds &&
    hallIds.length > 0 &&
    hallIds.some((id) => id > 0);

  const hallsSegment = hasSpecificHallSelection
    ? hallIds!.filter((id) => id > 0).join(",")
    : ALL_HALLS_URL_TOKEN;

  const hasSpecificUserSelection =
    userIds.length > 0 && userIds.some((id) => id > 0);

  const usersSegment = hasSpecificUserSelection
    ? userIds.filter((id) => id > 0).join(",")
    : ALL_USERS_URL_TOKEN;

  return routePaths.viewStatistics
    .replace(":type", type.toString())
    .replace(":dateFrom", dateFrom)
    .replace(":dateTo", dateTo)
    .replace(":userIds", usersSegment)
    .replace(":hallIds", hallsSegment);
};

// Planner

export const constructCalendarEventHref = (
  studioId: number,
  eventId: number,
  date?: Date
) => {
  return `${
    routePaths.calendar
  }?eventId=${eventId}&selectedStudio=${studioId}&date=${dateUtility.formatDateForHref(
    date,
    true
  )}`;
};

export const constructCalendarEventPreviewHref = (
  studioId: number,
  eventId: number,
  date?: Date
) => {
  return `${window.location.origin}${constructCalendarEventHref(
    studioId,
    eventId,
    date
  )}`;
};

export const constructPlannerEventHref = (id: number) => {
  return routePaths.plannerEvent.replace(":id", id.toString());
};

export const constructPlannerReservationHref = (
  eventId: number,
  reservationId: number
) => {
  return `${routePaths.plannerEvent.replace(
    ":id",
    eventId.toString()
  )}?reservationId=${reservationId}`;
};

export const constructPlannerEventEditHref = (id: number) => {
  return routePaths.plannerEdit.replace(":id", id.toString());
};

export const constructAddEventHref = (
  day: number,
  month: number,
  year: number
) => {
  return routePaths.plannerAdd
    .replace(":day", day.toString())
    .replace(":month", month.toString())
    .replace(":year", year.toString());
};

// Employees

export const constructEmployeeHref = (id: number) => {
  return routePaths.employeeSingle.replace(":id", id.toString());
};

export const constructEmployeeEditHref = (id: number) => {
  return routePaths.employeeEdit.replace(":id", id.toString());
};

// Plans

export const constructPlanHref = (id: number) => {
  return routePaths.planSingle.replace(":id", id.toString());
};

export const constructEditPlanHref = (id: number) => {
  return routePaths.planEdit.replace(":id", id.toString());
};

// Families

export const constructFamilyHref = (id: number) => {
  return routePaths.familySingle.replace(":id", id.toString());
};

export const constructEditFamilyHref = (id: number) => {
  return routePaths.familyEdit.replace(":id", id.toString());
};

// Users

export const constructUserHref = (id: number) => {
  return routePaths.userSingle.replace(":id", id.toString());
};

export const constructUserWorkoutsHref = (id: number) => {
  return routePaths.userWorkouts.replace(":id", id.toString());
};

export const constructUserBodyMetricsHref = (id: number) => {
  return routePaths.userBodyMetrics.replace(":id", id.toString());
};

export const constructUserGoalsHref = (id: number) => {
  return routePaths.userGoals.replace(":id", id.toString());
};

export const constructEditUserHref = (id: number) => {
  return routePaths.userEdit.replace(":id", id.toString());
};

// Tenants

export const constructTenantHref = (id: number) => {
  return routePaths.tenantSingle.replace(":id", id.toString());
};

export const constructEditTenantHref = (id: number) => {
  return routePaths.tenantEdit.replace(":id", id.toString());
};

// Studios

export const constructPortalStudioHref = (
  studio: IStudiosAdministrationStudioModel | undefined
) => {
  return `${studio?.url}${routePaths.portalAppointment}?selectedStudio=${studio?.id}`;
};

export const constructStudioHref = (id: number) => {
  return routePaths.studioSingle.replace(":id", id.toString());
};

export const constructEditStudioHref = (id: number) => {
  return routePaths.studioEdit.replace(":id", id.toString());
};

// Studio relations

export const constructEditStudioRelationHref = (id: number) => {
  return routePaths.studioRelationsEdit.replace(":id", id.toString());
};

// Planner

export const constructPlannerEventSignUpHref = (eventId: number) => {
  return routePaths.plannerEventSignUp.replace(":eventId", eventId.toString());
};

export const constructPlannerEventDuplicationHref = (eventId: number) => {
  return routePaths.plannerEventDuplication.replace(":id", eventId.toString());
};

export const constructPlannerEventEditSignUpHref = (eventId: number) => {
  return routePaths.plannerEventEditSignUp.replace(
    ":eventId",
    eventId.toString()
  );
};

// User Plan

export const constructUserPlanHref = (userPlanId: number) => {
  return routePaths.userPlan.replace(":userPlanId", userPlanId.toString());
};

export const constructUserPlanEditHref = (userPlanId: number) => {
  return routePaths.userPlanEdit.replace(":userPlanId", userPlanId.toString());
};

// My Family

export const constructMyFamilyAddHref = (id: number) => {
  return routePaths.portalMyFamilyAdd.replace(":familyId", id.toString());
};

export const constructMyFamilyEditHref = (id: number) => {
  return routePaths.portalMyFamilyEdit.replace(":familyId", id.toString());
};

// Reservation success

export const constructReservationSuccessHref = (reservationId: number) => {
  return `${routePaths.portalReservationSuccess}?reservationId=${reservationId}`;
};

// My reservations

export const constructMyReservationsReservationHref = (
  urlReservationId: number
) => {
  return `${routePaths.portalMyReservations}?urlReservationId=${urlReservationId}`;
};

// Cashdesk

export const constructCashdeskHref = (params?: {
  userId?: number;
  orderId?: number;
}) => {
  if (!params) return routePaths.cashdesk;
  const searchParams = new URLSearchParams();
  if (params.userId) searchParams.set("userId", params.userId.toString());
  if (params.orderId) searchParams.set("orderId", params.orderId.toString());
  const qs = searchParams.toString();
  return qs ? `${routePaths.cashdesk}?${qs}` : routePaths.cashdesk;
};

// Support Chat

export const constructSupportChatHref = (id: string) => {
  return routePaths.supportChat.replace(":id", id);
};

// Misc

/**
 * Where a tap on the studio's location goes. The address is free text the studio types, so a
 * Maps search over it stops finding the venue as soon as it carries a note ("вход откъм
 * двора") — hence the optional exact link, which wins whenever the studio saved one.
 *
 * The scheme is re-checked here and not only on the backend: this value is opened in the
 * visitor's browser, including on the public link page, and a stale or hand-crafted payload
 * must not be able to turn a click into a `javascript:` URL.
 */
export const generateMapsLink = (address?: string, exactUrl?: string | null) => {
  const trimmedExactUrl = exactUrl?.trim();
  // Case-insensitively, because the scheme is: the backend validates through Uri.Scheme,
  // which normalises "HTTPS://" to lowercase and stores it as typed. Matching the literal
  // prefix here would silently drop a link the API accepted and fall back to the broken
  // address search, with nothing shown to the studio that saved it.
  const scheme = trimmedExactUrl?.slice(0, 8).toLowerCase() ?? "";

  if (
    trimmedExactUrl &&
    (scheme.startsWith("https://") || scheme.startsWith("http://"))
  ) {
    return trimmedExactUrl;
  }

  if (!address) {
    return;
  }
  const baseUrl = "https://www.google.com/maps/search/?api=1&query=";
  const formattedAddress = encodeURIComponent(address);
  return `${baseUrl}${formattedAddress}`;
};

export const callPhoneNumber = (number?: string) => {
  if (!number) {
    return;
  }
  window.location.href = `tel:${number}`;
};

export const getDefaultRoutePath = (currentUser?: IUserModel | null) => {
  if (!currentUser) {
    return defaultRoutePathByRole[UserRole.User];
  }

  const userRole = getCurrentUserRole(currentUser!) ?? UserRole.User;

  return defaultRoutePathByRole[userRole];
};

export const defaultRoutePathByRole: Record<UserRole, string> = {
  [UserRole.User]: routePaths.portalAppointment,
  [UserRole.Employee_Instructor]: routePaths.dashboard,
  [UserRole.Employee_Instructor_Group_Events]: routePaths.dashboard,
  [UserRole.Employee_Manager]: routePaths.dashboard,
  [UserRole.Employee]: routePaths.dashboard,
  [UserRole.Tenant]: routePaths.dashboard,
  [UserRole.GlobalAdmin]: routePaths.dashboard,
};
